import axios from "axios"
import { defineStore } from "pinia"
import { reactive } from "vue"
import { Movie } from "../types/Movie"
import { Series } from "../types/Series"
import { isNativeDevice } from "../helper"
import { Router } from "vue-router"

interface TeaserState {
	windowCoords: {
		top: number
		left: number
		containerHeight?: number
	}
	isPreviewing: boolean
	selectedItem?: Movie | Series | undefined
	teaserSize?: string | undefined
	isClosing: boolean | undefined
	animationStartDelay: number
	showPreviewSection: boolean
	playerRef?: any
	playerRefLarge?: any
	positionClass?: string
	isFirstVisibleElement?: boolean
	isLastVisibleElement?: boolean
	teaserKey?: string | number | undefined
	teaserHeight: number
	originalTop?: number
	timestamps: Timestamp[]
}

interface Timestamp {
	id: number
	type: string
	timestamp: number
}

export const useTeaserStore = defineStore("useTeaserStore", () => {
	const options = {
		autoplay: true,
		language: "el",
		muted: true,
		loop: true,
		// src: "https://vjs.zencdn.net/v/oceans.mp4",
		// src: movie.value?.field_trailer?.uri ?? "https://vjs.zencdn.net/v/oceans.mp4",
		controls: false,
		playsinline: true,
		disablePictureInPicture: true,
		spatialNavigation: {
			enabled: true,
			horizontalSeek: true,
		},
	}

	const heroSectionState = reactive({
		activeItem: null as Movie | Series | null,
		isMuted: true,
	})

	const state = reactive<TeaserState>({
		windowCoords: {
			top: 0,
			left: 0,
		},
		isPreviewing: false,
		selectedItem: undefined,
		teaserSize: undefined,
		isClosing: undefined,
		animationStartDelay: isNativeDevice ? 0 : 120,
		showPreviewSection: false,
		teaserHeight: 438,
		timestamps: [],
	})

	const setSelectedItem = (item: Movie | Series | undefined, teaserSize?: string) => {
		state.selectedItem = item
		state.teaserSize = teaserSize

		if (item === undefined) {
			state.isPreviewing = false
			state.showPreviewSection = false
			state.isClosing = undefined
		}
	}

	const addTimestamp = (timestamp: number, type: string, id: number) => {
		const existingIndex = state.timestamps.findIndex((t) => t.id === id)
		if (existingIndex !== -1) {
			state.timestamps[existingIndex] = { id, type, timestamp }
		} else {
			state.timestamps.push({ id, type, timestamp })
		}
	}

	const setTeaserPosition = (element: HTMLElement | null) => {
		if (!element) return
		const rect = element.getBoundingClientRect()
		// state.windowCoords.top = rect.height - state.teaserHeight / 2
		state.originalTop = rect.top + window.scrollY
		state.windowCoords.top = rect.top + window.scrollY
		state.windowCoords.left = rect.left + window.scrollX
		const replicaItem = document.querySelector(".teaser-replica") as HTMLElement | null
		const mainContainer = element.closest(".row-slider-section") as HTMLElement | null
		const transformYPlacement = -(state.teaserHeight - rect.height) / 2 // mainContainer && replicaItem ? rect.height - mainContainer.clientHeight : 0
		// set a css variable for container height
		// if (state.windowCoords.containerHeight) {
		if (element.classList.contains("teaser-with-number")) {
			// detect spacing for element with it's parentElement by subtracting left positions
			const parentElement = element.parentElement as HTMLElement | null

			const spacing = parentElement
				? Math.abs(parentElement.getBoundingClientRect().left - element.getBoundingClientRect().left) + 20 // 20 is extra margin
				: 0
			document.documentElement.style.setProperty("--teaser-numspacing-width", `${spacing}px`)
		}
		document.documentElement.style.setProperty("--teaser-current-width", `${rect.width}px`)
		document.documentElement.style.setProperty("--main-container-width", `${mainContainer?.clientWidth}px`)
		document.documentElement.style.setProperty("--teaser-container-height", `${transformYPlacement}px`)
		// }
	}

	const handleMouseOver = () => {
		state.isPreviewing = true
		state.showPreviewSection = true
		state.isClosing = undefined
		state.windowCoords.top = state.originalTop || state.windowCoords.top
	}

	const handleWebMouseLeave = () => {
		state.isPreviewing = false
		state.showPreviewSection = false
		setTimeout(() => {
			state.isClosing = false
		}, 300)
	}

	/**
	 * Stats playing the selected item, based on its type.
	 * @param type
	 * @param item
	 * @param episode_nid
	 */
	const play = (type: string, item: Movie | Series, episode_nid?: number, router?: Router) => {
		// console.log("Play item from teaser store:", type, item, episode_nid)
		if (!router) return
		router.push({
			name: "Watch",
			params: {
				type: type.includes("movie") ? "movie" : "episode",
				id: type.includes("movie") ? item.drupal_internal__nid : episode_nid,
			},
		})
	}

	return {
		state,
		heroSectionState,
		options,
		play,
		addTimestamp,
		setSelectedItem,
		setTeaserPosition,
		handleWebMouseLeave,
		handleMouseOver,
	}
})
